import { AlertTriangle } from "lucide-react";
import { useCallback, useRef } from "react";
import type { SceneObjectValidationIssue } from "../modeling/sceneObjectValidation.ts";
import { Button } from "./Button.tsx";
import { createImperativeDialog } from "./imperativeDialog.tsx";
import { ModalActions, ModalHeader, ModalShell } from "./ModalShell.tsx";
import {
  COLOR_CAUTION,
  COLOR_TEXT,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
} from "./tokens.ts";

export type InvalidSceneObjectsResult = "cleanup" | "back";

interface InvalidSceneObjectsArgs {
  sceneName: string;
  issues: SceneObjectValidationIssue[];
}

const invalidSceneObjectsDialog = createImperativeDialog<
  InvalidSceneObjectsArgs,
  InvalidSceneObjectsResult
>({
  render: (args, close) => (
    <InvalidSceneObjectsDialogBody args={args} close={close} />
  ),
});

export function showInvalidSceneObjectsDialog(
  sceneName: string,
  issues: SceneObjectValidationIssue[],
): Promise<InvalidSceneObjectsResult> {
  return invalidSceneObjectsDialog.show({ sceneName, issues });
}

export const InvalidSceneObjectsDialog = invalidSceneObjectsDialog.Host;

function InvalidSceneObjectsDialogBody({
  args,
  close,
}: {
  args: InvalidSceneObjectsArgs;
  close: (result: InvalidSceneObjectsResult) => void;
}) {
  const cleanupRef = useRef<HTMLButtonElement>(null);
  const issueCount = args.issues.length;
  const previewNames = args.issues.slice(0, 5).map((issue) => issue.objectName);
  const hiddenCount = issueCount - previewNames.length;

  const closeBack = useCallback(() => close("back"), [close]);
  const closeCleanup = useCallback(() => close("cleanup"), [close]);

  return (
    <ModalShell
      open
      onClose={closeBack}
      onConfirm={closeCleanup}
      width={420}
      autoFocusRef={cleanupRef}
    >
      <ModalHeader
        icon={AlertTriangle}
        iconColor={COLOR_CAUTION}
        title="Invalid Objects Found"
      />

      <div
        style={{
          fontSize: FONT_SIZE_BODY,
          color: COLOR_TEXT_SECONDARY,
          lineHeight: 1.55,
        }}
      >
        <strong style={{ color: COLOR_TEXT }}>{args.sceneName}</strong> contains{" "}
        <strong style={{ color: COLOR_TEXT }}>
          {issueCount} invalid {issueCount === 1 ? "object" : "objects"}
        </strong>
        .
        <br />
        <br />
        Clean Up will remove {issueCount === 1 ? "it" : "them"} so the rest of
        the scene can open.
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: "10px 12px",
          borderRadius: 10,
          background: "rgba(255,255,255,0.04)",
        }}
      >
        {previewNames.map((name) => (
          <div
            key={name}
            style={{ fontSize: FONT_SIZE_BODY, color: COLOR_TEXT_SECONDARY }}
          >
            {name}
          </div>
        ))}
        {hiddenCount > 0 && (
          <div
            style={{ fontSize: FONT_SIZE_BODY, color: COLOR_TEXT_SECONDARY }}
          >
            and {hiddenCount} more
          </div>
        )}
      </div>

      <ModalActions>
        <Button onClick={closeBack} variant="secondary">
          Back to Library
        </Button>
        <Button ref={cleanupRef} onClick={closeCleanup} variant="primary">
          Clean Up
        </Button>
      </ModalActions>
    </ModalShell>
  );
}
